import { useCallback, useEffect, useRef, useState } from "react";

/* ------------------------------------------------------------------
   Motion preference — read once, then live-updated.
------------------------------------------------------------------- */
const REDUCED_QUERY = "(prefers-reduced-motion: reduce)";

export function prefersReducedMotion() {
  return (
    typeof window !== "undefined" &&
    window.matchMedia &&
    window.matchMedia(REDUCED_QUERY).matches
  );
}

export function useMediaQuery(query) {
  const [matches, setMatches] = useState(() =>
    typeof window !== "undefined" && window.matchMedia
      ? window.matchMedia(query).matches
      : false
  );

  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const mql = window.matchMedia(query);
    const onChange = (e) => setMatches(e.matches);
    setMatches(mql.matches);
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, [query]);

  return matches;
}

export const useReducedMotion = () => useMediaQuery(REDUCED_QUERY);

/* ------------------------------------------------------------------
   Reveal on scroll.
   One shared IntersectionObserver for every element on the page —
   cheaper than one observer per node, and zero scroll listeners.
------------------------------------------------------------------- */
let sharedObserver = null;

function getObserver() {
  if (sharedObserver || typeof IntersectionObserver === "undefined") {
    return sharedObserver;
  }
  sharedObserver = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          entry.target.dataset.visible = "true";
          sharedObserver.unobserve(entry.target); // reveal once, then stop watching
        }
      }
    },
    { rootMargin: "0px 0px -8% 0px", threshold: 0.08 }
  );
  return sharedObserver;
}

export function useReveal(delay = 0) {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;

    if (delay) el.style.setProperty("--reveal-delay", `${delay}ms`);

    const observer = getObserver();
    if (!observer || prefersReducedMotion()) {
      el.dataset.visible = "true";
      return undefined;
    }

    // observe() is idempotent, so re-running this effect is safe.
    observer.observe(el);
    return () => observer.unobserve(el);
  }, [delay]);

  return ref;
}

/* ------------------------------------------------------------------
   Theme — dark by default, persisted, respects the OS on first visit.
------------------------------------------------------------------- */
const THEME_KEY = "oa-theme";

function readTheme() {
  if (typeof window === "undefined") return "dark";
  try {
    const saved = window.localStorage.getItem(THEME_KEY);
    if (saved === "dark" || saved === "light") return saved;
  } catch (_) {
    /* private mode — nothing saved, fall through */
  }
  // Dark is the signature look; light is opt-in via the toggle.
  return "dark";
}

export function useTheme() {
  const [theme, setTheme] = useState(readTheme);

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) meta.setAttribute("content", theme === "light" ? "#f6f7fb" : "#05060a");
    try {
      window.localStorage.setItem(THEME_KEY, theme);
    } catch (_) {
      /* nothing to persist to — the in-memory theme still applies */
    }
  }, [theme]);

  const toggle = useCallback(
    () => setTheme((t) => (t === "dark" ? "light" : "dark")),
    []
  );

  return [theme, toggle];
}

/* ------------------------------------------------------------------
   Typewriter — cycles a list of strings. One timeout at a time.
------------------------------------------------------------------- */
export function useTypewriter(words, { type = 62, erase = 32, hold = 1900 } = {}) {
  const [text, setText] = useState("");
  const reduced = useReducedMotion();

  useEffect(() => {
    if (reduced) {
      setText(words[0] || "");
      return undefined;
    }

    let index = 0;
    let char = 0;
    let erasing = false;
    let timer;

    const tick = () => {
      const word = words[index % words.length];
      char += erasing ? -1 : 1;
      setText(word.slice(0, char));

      if (!erasing && char === word.length) {
        erasing = true;
        timer = setTimeout(tick, hold);
        return;
      }
      if (erasing && char === 0) {
        erasing = false;
        index += 1;
        timer = setTimeout(tick, 260);
        return;
      }
      timer = setTimeout(tick, erasing ? erase : type);
    };

    timer = setTimeout(tick, 420);
    return () => clearTimeout(timer);
  }, [words, type, erase, hold, reduced]);

  return text;
}

/* ------------------------------------------------------------------
   Count-up — runs on rAF, only once the element is on screen.
------------------------------------------------------------------- */
export function useCountUp(target, { duration = 1500, decimals = 0 } = {}) {
  const [value, setValue] = useState(0);
  const ref = useRef(null);
  const reduced = useReducedMotion();

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;

    if (reduced || typeof IntersectionObserver === "undefined") {
      setValue(target);
      return undefined;
    }

    let frame;
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries[0].isIntersecting) return;
        observer.disconnect();

        const start = performance.now();
        const step = (now) => {
          const t = Math.min((now - start) / duration, 1);
          // easeOutExpo — fast, then settles
          const eased = t === 1 ? 1 : 1 - Math.pow(2, -10 * t);
          setValue(Number((target * eased).toFixed(decimals)));
          if (t < 1) frame = requestAnimationFrame(step);
        };
        frame = requestAnimationFrame(step);
      },
      { threshold: 0.4 }
    );

    observer.observe(el);
    return () => {
      observer.disconnect();
      cancelAnimationFrame(frame);
    };
  }, [target, duration, decimals, reduced]);

  return [value, ref];
}

/* ------------------------------------------------------------------
   Pointer spotlight on a card. Writes CSS vars inside one rAF —
   never triggers layout, never re-renders React.
------------------------------------------------------------------- */
export function useSpotlight() {
  const ref = useRef(null);
  const frame = useRef(0);

  const onPointerMove = useCallback((event) => {
    const el = ref.current;
    if (!el || frame.current) return;
    const { clientX, clientY } = event;
    frame.current = requestAnimationFrame(() => {
      frame.current = 0;
      const rect = el.getBoundingClientRect();
      el.style.setProperty("--mx", `${clientX - rect.left}px`);
      el.style.setProperty("--my", `${clientY - rect.top}px`);
    });
  }, []);

  useEffect(() => () => cancelAnimationFrame(frame.current), []);

  return { ref, onPointerMove };
}

/* ------------------------------------------------------------------
   Subtle 3D tilt toward the pointer. Shares the spotlight's rAF budget:
   one frame in flight, CSS vars only, no React re-render.
------------------------------------------------------------------- */
export function useTilt({ max = 5 } = {}) {
  const ref = useRef(null);
  const frame = useRef(0);
  const reduced = useReducedMotion();

  const onPointerMove = useCallback(
    (event) => {
      const el = ref.current;
      if (!el || reduced || frame.current) return;
      const { clientX, clientY } = event;
      frame.current = requestAnimationFrame(() => {
        frame.current = 0;
        const rect = el.getBoundingClientRect();
        const px = (clientX - rect.left) / rect.width;
        const py = (clientY - rect.top) / rect.height;
        el.style.setProperty("--mx", `${clientX - rect.left}px`);
        el.style.setProperty("--my", `${clientY - rect.top}px`);
        el.style.setProperty("--ry", `${(px - 0.5) * max * 2}deg`);
        el.style.setProperty("--rx", `${(0.5 - py) * max * 2}deg`);
      });
    },
    [max, reduced]
  );

  const onPointerLeave = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    el.style.setProperty("--ry", "0deg");
    el.style.setProperty("--rx", "0deg");
  }, []);

  useEffect(() => () => cancelAnimationFrame(frame.current), []);

  return { ref, onPointerMove, onPointerLeave };
}

/* ------------------------------------------------------------------
   Lock body scroll while an overlay is open.
------------------------------------------------------------------- */
export function useScrollLock(locked) {
  useEffect(() => {
    if (!locked) return undefined;
    const { overflow, paddingRight } = document.body.style;
    const gap = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.overflow = "hidden";
    if (gap > 0) document.body.style.paddingRight = `${gap}px`;
    return () => {
      document.body.style.overflow = overflow;
      document.body.style.paddingRight = paddingRight;
    };
  }, [locked]);
}
